/**
 * Maximum recursion depth for flattening telemetry data to prevent infinite loops
 */
export const MAX_FLATTEN_DEPTH = 5;

/**
 * Flattens nested objects in telemetry data to one level recursively.
 * Removes nested objects and promotes their properties to the top level.
 * Always uses parentKey_childKey naming for nested object properties.
 * Optimized for the common case where data is already flat.
 *
 * @param data The telemetry data object to flatten
 * @param maxDepth Maximum recursion depth to prevent infinite loops (default: MAX_FLATTEN_DEPTH)
 * @returns A new object with all nested objects flattened to one level, using parentKey_childKey naming for all nested properties
 */
export function flattenTelemetryData(
    data: Record<string, any>,
    maxDepth: number = MAX_FLATTEN_DEPTH
): Record<string, any> {
    // Early validation
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return {};
    }

    return flattenTelemetryDataRecursive(data, '', 0, maxDepth);
}
/**
 * Checks if a value is a plain object (not null, not array, not primitive)
 */
function isPlainObject(value: any): value is Record<string, any> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Applies prefix to all keys in an object
 */
function applyPrefixToKeys(data: Record<string, any>, prefix: string): Record<string, any> {
    if (!prefix) {
        return data;
    }

    const prefixedData: Record<string, any> = {};
    for (const [key, value] of Object.entries(data)) {
        prefixedData[`${prefix}${key}`] = value;
    }
    return prefixedData;
}

/**
 * Recursive helper function for flattening telemetry data
 * @param data The data to flatten
 * @param prefix Current key prefix for nested properties
 * @param currentDepth Current recursion depth
 * @param maxDepth Maximum allowed recursion depth
 * @returns Flattened object
 */
function flattenTelemetryDataRecursive(
    data: Record<string, any>,
    prefix: string,
    currentDepth: number,
    maxDepth: number
): Record<string, any> {
    // Prevent infinite recursion by checking depth limit early
    if (currentDepth >= maxDepth) {
        return applyPrefixToKeys(data, prefix);
    }

    const flattened: Record<string, any> = {};
    let hasNestedObjects = false;

    // Single iteration to both check for nested objects and process entries
    for (const [key, value] of Object.entries(data)) {
        if (isPlainObject(value)) {
            // Check if the object has enumerable properties to flatten
            const objectEntries = Object.entries(value);
            if (objectEntries.length > 0) {
                hasNestedObjects = true;
                // Recursively flatten nested objects
                const currentPrefix = prefix ? `${prefix}${key}_` : `${key}_`;
                const nestedFlattened = flattenTelemetryDataRecursive(
                    value,
                    currentPrefix,
                    currentDepth + 1,
                    maxDepth
                );
                // Merge the flattened nested object into our result
                Object.assign(flattened, nestedFlattened);
            } else {
                // Object with no enumerable properties (like Date) - treat as primitive
                const finalKey = prefix ? `${prefix}${key}` : key;
                flattened[finalKey] = value;
            }
        } else {
            // Non-object value - add with appropriate prefix
            const finalKey = prefix ? `${prefix}${key}` : key;
            flattened[finalKey] = value;
        }
    }

    // Fast path: if no nested objects were found, return the appropriately prefixed original data
    if (!hasNestedObjects) {
        return applyPrefixToKeys(data, prefix);
    }

    return flattened;
}
